/**
 * Compatibilité Ummah → 2026.
 *
 * Helpers qui lisent les nouvelles clés config 2026 en priorité, et tombent
 * sur les anciennes clés Ummah en fallback. Permet à un site migré depuis
 * Ummah de bénéficier de ses anciennes configurations sans rien re-saisir.
 *
 * Les anciennes clés sont éditables côté admin via la modale
 * "Anciennes configurations" (cf. unisoft-admin/.../theme/ModalLegacyConfig.js).
 */

/**
 * Récupère les slides du Hero 2026.
 *   - priorité : config["hero.slides"] (nouveau format riche)
 *   - fallback : config["hero.carousel"] Ummah, transformé à la volée
 *
 * Mapping legacy → 2026 :
 *   { image, title, textLink, link }  →
 *   { id, image, title, btn1_label, btn1_url }
 *
 * Les nouveaux champs (eyebrow, subtitle, description, btn2_*, icons) ne sont
 * pas remplis : l'utilisateur les ajoutera quand il rééditera ses slides.
 */
export function readHeroSlides(config) {
    if (!config) return [];
    if (Array.isArray(config["hero.slides"])) return config["hero.slides"];

    const legacy = config["hero.carousel"];
    if (!Array.isArray(legacy)) return [];

    return legacy
        .filter(s => s && s.image)
        .map((s, i) => ({
            id: s.image || `legacy-${i}`,
            image: s.image,
            title: s.title || "",
            btn1_label: s.textLink || "",
            btn1_url: s.link || "",
        }));
}

/**
 * Récupère les sections de la home.
 *   - priorité : config["home.sections"] (configuration explicite via /web/theme)
 *   - fallback : `defaults` passés en argument, avec application des anciennes
 *     clés Ummah `home.sections.disable.*` pour masquer les sections legacy.
 *
 * Mapping legacy → 2026 :
 *   home.sections.disable.don           → CAISSES_PREVIEW hidden
 *   home.sections.disable.auto_content  → ARTICLES_PREVIEW hidden
 */
export function readHomeSections(config, defaults) {
    if (!config) return defaults || [];
    if (Array.isArray(config["home.sections"])) return config["home.sections"];

    const base = Array.isArray(defaults) ? defaults : [];
    if (base.length === 0) return base;

    const disableDon = config["home.sections.disable.don"] === true;
    const disableAuto = config["home.sections.disable.auto_content"] === true;

    if (!disableDon && !disableAuto) return base;

    return base.map(s => {
        if (s.type === "CAISSES_PREVIEW" && disableDon) return { ...s, hidden: true };
        if (s.type === "ARTICLES_PREVIEW" && disableAuto) return { ...s, hidden: true };
        return s;
    });
}

/**
 * Indique si un menu auto (Don, Horaires, Événements, Galeries, Abonnements)
 * doit être affiché. Convention : `menu.<key>.active !== false` (default ON).
 *
 *   isMenuActive(config, "don")      → config["menu.don.active"] !== false
 *   isMenuActive(config, "galerie")  → config["menu.galerie.active"] !== false
 */
export function isMenuActive(config, key) {
    return config?.[`menu.${key}.active`] !== false;
}

/**
 * Label de la caisse principale (override pour CaissesPreview eyebrow/title).
 * Legacy Ummah : config["don.main.label"] (ex: "Don Spontané").
 */
export function readMainCaisseLabel(config) {
    return config?.["don.main.label"] || null;
}
